import type { Conversation } from '@/types';

/**
 * Choose the conversation record to drive the planner gate, reconciling the
 * useChatWorker sidebar list row (`listRow`) against the self-healing by-id
 * query (`byId`).
 *
 * The list row has no anti-entropy channel: it is fetched once on mount and
 * thereafter only updated by delivered `#conversation#` WS events, so a dropped
 * active->idle MODIFY wedges it 'active' until a full remount. The by-id query
 * self-heals via a 60s reconcile poll + WS-invalidate.
 *
 * Resolution:
 *  - If only one source exists, return it (legacy `find(...) || byId` behavior).
 *  - If both exist, return the list row — EXCEPT apply a DOWNGRADE-ONLY status
 *    override: when the list row is 'active' but the by-id record has a known
 *    non-'active' status, trust the by-id status (it is the channel that heals).
 *  - Never override in the upgrade direction: on the send path the list row
 *    flips to 'active' via WS faster than the by-id cache invalidates, so an
 *    upgrade override would briefly resurrect a stale 'idle' and re-open Enter
 *    mid-run.
 */
export function mergeSelectedConversation(
  listRow: Conversation | undefined,
  byId: Conversation | undefined
): Conversation | undefined {
  if (!listRow) return byId;
  if (!byId) return listRow;

  if (listRow.status === 'active' && byId.status && byId.status !== 'active') {
    return { ...listRow, status: byId.status };
  }
  return listRow;
}
